import React, { FC } from "react";
import { Property } from "containers/ListingStayPage/SectionGridHasMap";
import pinIcon from "images/card/pin-icon.svg";
import getImageVariantUrl from "utils/getImageVariantUrl";

export interface ClusterPropertyListProps {
  properties: Property[];
  location: string;
  selectedPropertyId?: string;
  maxHeight?: number;
  onSelectProperty: (property: Property) => void;
  onClose: () => void;
}


const getPrice = (property: Property): number => {
  return property.avgRatePerDay
    ? Math.round(property.avgRatePerDay)
    : property.base_price;
};

const ClusterPropertyList: FC<ClusterPropertyListProps> = ({
  properties,
  location,
  selectedPropertyId,
  maxHeight,
  onSelectProperty,
  onClose,
}) => {
  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const original = img.dataset.original || "";
    // Prefer the _compress variant (full resolution, lighter JPEG); if it is
    // missing, fall back to the full original.
    if (original && img.src !== original) {
      img.src = original;
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #d6d6d6",
        borderRadius: 6,
        padding: 16,
        width: "100%",
        maxWidth: 340,
        maxHeight: maxHeight || undefined,
        overflowY: "auto" as const,
        boxShadow: "0px 0px 6px rgba(42,54,84,0.3)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <img
            src={pinIcon}
            alt=""
            style={{ width: 8, height: 10, flexShrink: 0 }}
          />
          <span
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: 12,
              lineHeight: "24px",
              color: "#01152E",
            }}
          >
            {location}
          </span>
        </div>
        <span
          style={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 600,
            fontSize: 8,
            lineHeight: "24px",
            color: "#01152E",
            textTransform: "uppercase",
            letterSpacing: "0.8px",
          }}
        >
          {properties.length} PROPERTIES
        </span>
      </div>

      {/* Property rows */}
      {properties.map((property, index) => {
        const image = (property as any).galleryImgs?.[0] || (property as any).image;
        const price = getPrice(property);
        const isActive = selectedPropertyId === property._id;

        return (
          <React.Fragment key={property._id}>
            {index > 0 && (
              <div
                style={{
                  width: "100%",
                  height: 1,
                  backgroundColor: "#d6d6d6",
                  marginTop: 14,
                  marginBottom: 14,
                }}
              />
            )}
            <div
              style={{
                display: "flex",
                gap: 12,
                cursor: "pointer",
                padding: "6px 4px",
                borderRadius: 4,
                backgroundColor: isActive ? "#f5f0e8" : "transparent",
                transition: "background-color 0.15s ease",
              }}
              onClick={() => onSelectProperty(property)}
            >
              {/* Thumbnail */}
              <div
                style={{
                  width: 80,
                  height: 53,
                  borderRadius: 4,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <img
                  src={
                    image
                      ? getImageVariantUrl(image, "_compress")
                      : "/sly_0470-sly_0472.jpg"
                  }
                  data-original={image}
                  alt={property.title || ""}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  loading="lazy"
                  onError={handleImgError}
                />
              </div>

              {/* Info */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <p
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    fontSize: 12,
                    lineHeight: "16px",
                    color: "#01152E",
                    margin: 0,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {property.title}
                </p>
                <span
                  style={{
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 500,
                    fontSize: 10,
                    lineHeight: "14px",
                    color: "#445162",
                  }}
                >
                  Sleeps {property.sleeps}
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      fontSize: 12,
                      color: "#01152E",
                    }}
                  >
                    &euro;
                  </span>
                  <span
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 600,
                      fontSize: 12,
                      lineHeight: "16px",
                      color: "#01152E",
                    }}
                  >
                    {price}
                  </span>
                  <span
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 400,
                      fontSize: 10,
                      lineHeight: "16px",
                      color: "#01152E",
                    }}
                  >
                    /night
                  </span>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ClusterPropertyList;
